import React, { useState, useEffect } from 'react';
import { api, formatCurrency, formatDate, capexCategories, paymentModes, getCategoryLabel } from '../lib/utils';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { FolderKanban, Plus, IndianRupee, Calendar, Target, TrendingUp, BarChart3, Search, Pencil, Trash2, Landmark, Link2, Unlink, Users } from 'lucide-react';
import { Autocomplete } from '../components/ui/autocomplete';

export default function Projects() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [projects, setProjects] = useState([]);
  const [ventures, setVentures] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectExpenses, setProjectExpenses] = useState([]);
  const [linkedBankExpenses, setLinkedBankExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [roiData, setRoiData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editProject, setEditProject] = useState(null);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => {
    if (selectedProject) {
      fetchProjectExpenses(selectedProject.project_id);
      fetchROI(selectedProject.project_id);
    }
  }, [selectedProject]);

  const fetchData = async () => {
    try {
      const [projRes, ventRes, locRes] = await Promise.all([api.get('/projects'), api.get('/ventures'), api.get('/locations')]);
      setProjects(projRes.data);
      setVentures(ventRes.data);
      setLocations(locRes.data);
      if (projRes.data.length > 0 && !selectedProject) setSelectedProject(projRes.data[0]);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const fetchProjectExpenses = async (id) => {
    try {
      const [expRes, allTxnRes] = await Promise.all([
        api.get(`/projects/${id}/expenses`),
        api.get(`/projects/${id}/all-transactions`).catch(() => ({ data: { bank_expenses: [] } }))
      ]);
      setProjectExpenses(expRes.data);
      setLinkedBankExpenses(allTxnRes.data.bank_expenses || []);
    } catch {}
  };
  const fetchROI = async (id) => {
    try { const res = await api.get(`/projects/${id}/roi`); setRoiData(res.data); } catch { setRoiData(null); }
  };

  const getVentureName = (id) => ventures.find(v => v.venture_id === id)?.name || 'Unknown';

  const handleDeleteProject = async (proj) => {
    if (!window.confirm(`Delete project ${proj.name}? All expenses will also be deleted.`)) return;
    try {
      await api.delete(`/projects/${proj.project_id}`);
      toast.success('Project deleted');
      if (selectedProject?.project_id === proj.project_id) setSelectedProject(null);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteExpense = async (exp) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await api.delete(`/projects/expenses/${exp.expense_id}`); toast.success('Deleted'); fetchData(); fetchProjectExpenses(selectedProject.project_id); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="projects-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">Projects</h1>
          <p className="text-zinc-500 mt-1">Track capital investments and project setup costs</p>
        </div>
        {isOwner && <Button onClick={() => { setEditProject(null); setAddProjectOpen(true); }} className="rounded-xl" data-testid="add-project-btn"><Plus className="w-4 h-4 mr-2" />New Project</Button>}
      </div>

      {projects.length === 0 ? (
        <Card className="roch-card"><div className="empty-state"><div className="empty-state-icon"><FolderKanban className="w-6 h-6" /></div><h3 className="font-medium text-zinc-900 mb-1">No projects yet</h3><p className="text-sm text-zinc-500 mb-4">Track capital investments for new ventures</p><Button onClick={() => setAddProjectOpen(true)}>Create Project</Button></div></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-4">
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input placeholder="Search projects..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="projects-search" /></div>
            {projects.filter(p => !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase())).map((project) => (
              <Card key={project.project_id} className={`roch-card cursor-pointer transition-all ${selectedProject?.project_id === project.project_id ? 'ring-2 ring-zinc-900' : 'hover:shadow-md'}`} onClick={() => setSelectedProject(project)}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div><h3 className="font-medium text-zinc-900">{project.name}</h3><p className="text-xs text-zinc-500">{(project.venture_ids || [project.venture_id]).filter(Boolean).map(id => getVentureName(id)).join(', ') || 'No venture'}</p></div>
                    <div className="flex items-center gap-1">
                      <span className={`badge ${project.is_completed ? 'badge-success' : 'badge-warning'}`}>{project.is_completed ? 'Done' : 'Active'}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); setEditProject(project); setAddProjectOpen(true); }}><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={(e) => { e.stopPropagation(); handleDeleteProject(project); }}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                  <div className="mt-3"><div className="flex justify-between text-xs text-zinc-500 mb-1"><span>{formatCurrency(project.total_spent)} spent</span><span>{formatCurrency(project.budget)} budget</span></div><Progress value={Math.min(100, (project.total_spent / project.budget) * 100)} className="h-1.5" /></div>
                </CardContent>
              </Card>
            ))}
          </div>

          {selectedProject && (
            <div className="lg:col-span-2 space-y-6">
              <Card className="roch-card">
                <CardHeader className="pb-2"><div className="flex items-center justify-between"><CardTitle className="text-xl">{selectedProject.name}</CardTitle><Button onClick={() => { setEditExpense(null); setAddExpenseOpen(true); }} size="sm" className="rounded-xl"><Plus className="w-4 h-4 mr-1" />Add Expense</Button></div></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div><div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Budget</div><div className="text-lg font-semibold">{formatCurrency(selectedProject.budget)}</div></div>
                    <div><div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Spent</div><div className="text-lg font-semibold text-amber-600">{formatCurrency(selectedProject.total_spent)}</div></div>
                    <div><div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Remaining</div><div className={`text-lg font-semibold ${selectedProject.budget - selectedProject.total_spent >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(selectedProject.budget - selectedProject.total_spent)}</div></div>
                    <div><div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Progress</div><div className="text-lg font-semibold">{Math.round((selectedProject.total_spent / selectedProject.budget) * 100)}%</div></div>
                  </div>
                  {selectedProject.loan_amount > 0 && (
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-4 flex items-center gap-3">
                      <Landmark className="w-5 h-5 text-blue-600" />
                      <div>
                        <span className="text-sm font-medium text-blue-800">Loan: {formatCurrency(selectedProject.loan_amount)}</span>
                        {selectedProject.loan_bank_account_id && <span className="text-xs text-blue-600 ml-2">via bank account</span>}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-6 text-sm text-zinc-500">
                    <div className="flex items-center gap-1"><Calendar className="w-4 h-4" />Started: {formatDate(selectedProject.start_date)}</div>
                    {selectedProject.expected_opening_date && <div className="flex items-center gap-1"><Target className="w-4 h-4" />Expected: {formatDate(selectedProject.expected_opening_date)}</div>}
                  </div>
                </CardContent>
              </Card>

              {roiData && (
                <Card className="roch-card" data-testid="roi-analysis">
                  <CardHeader className="pb-2"><CardTitle className="text-base font-medium flex items-center gap-2"><TrendingUp className="w-4 h-4" />ROI Analysis</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="p-3 bg-zinc-50 rounded-lg"><div className="text-xs text-zinc-500 mb-1">Total Invested</div><div className="text-base font-semibold">{formatCurrency(roiData.investment?.total_invested)}</div></div>
                      <div className="p-3 bg-zinc-50 rounded-lg"><div className="text-xs text-zinc-500 mb-1">Revenue</div><div className="text-base font-semibold text-green-600">{formatCurrency(roiData.returns?.total_revenue)}</div></div>
                      <div className="p-3 bg-zinc-50 rounded-lg"><div className="text-xs text-zinc-500 mb-1">Net Operating Income</div><div className={`text-base font-semibold ${roiData.returns?.net_operating_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(roiData.returns?.net_operating_income)}</div></div>
                      <div className={`p-3 rounded-lg ${roiData.roi_pct >= 0 ? 'bg-green-50' : 'bg-red-50'}`}><div className="text-xs text-zinc-500 mb-1">ROI</div><div className={`text-lg font-bold ${roiData.roi_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>{roiData.roi_pct}%</div></div>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-zinc-500">
                      <div className="flex items-center gap-1"><BarChart3 className="w-4 h-4" />Budget Used: {roiData.investment?.budget_utilization_pct}%</div>
                      {roiData.payback_months !== null && <div>Est. Payback: {roiData.payback_months} months</div>}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="roch-card">
                <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Project Expenses</CardTitle></CardHeader>
                <CardContent className="p-0">
                  {projectExpenses.length === 0 ? <div className="text-center py-8 text-sm text-zinc-500">No expenses recorded</div> : (
                    <div className="roch-table-wrap">
                      <table className="roch-table">
                        <thead><tr><th>Date</th><th>Vendor</th><th>Category</th><th>Description</th><th className="text-right">Amount</th><th className="w-16"></th></tr></thead>
                        <tbody>
                          {projectExpenses.map((exp) => (
                            <tr key={exp.expense_id}>
                              <td className="text-zinc-500 whitespace-nowrap">{formatDate(exp.date)}</td>
                              <td className="font-medium whitespace-nowrap">{exp.vendor}</td>
                              <td className="whitespace-nowrap"><span className="badge badge-default">{getCategoryLabel(exp.category, capexCategories)}</span>{exp.linked_expense_id && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">bank-linked</span>}</td>
                              <td className="text-zinc-600">{exp.description}</td>
                              <td className="text-right font-medium whitespace-nowrap">{formatCurrency(exp.amount)}</td>
                              <td>
                                <div className="flex items-center gap-0.5">
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-500" onClick={() => { setEditExpense(exp); setAddExpenseOpen(true); }} title="Edit" data-testid={`edit-project-expense-${exp.expense_id}`}>
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteExpense(exp)} title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Spend by Vendor — cumulative totals */}
              {(projectExpenses.length > 0 || linkedBankExpenses.length > 0) && (
                <SpendByVendorCard
                  projectExpenses={projectExpenses}
                  linkedBankExpenses={linkedBankExpenses}
                />
              )}

              {/* Linked Bank Transactions */}
              {linkedBankExpenses.length > 0 && (
                <Card className="roch-card" data-testid="linked-bank-txns">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-medium flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-blue-600" />
                      Linked Bank Transactions
                      <span className="text-xs text-zinc-400 font-normal">({linkedBankExpenses.length} from bank imports)</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="roch-table-wrap">
                      <table className="roch-table">
                        <thead><tr><th>Date</th><th>Description</th><th>Category</th><th className="text-right">Amount</th><th className="w-16"></th></tr></thead>
                        <tbody>
                          {linkedBankExpenses.map((tx) => (
                            <tr key={tx.expense_id} className="bg-blue-50/30">
                              <td className="text-zinc-500 whitespace-nowrap">{formatDate(tx.date)}</td>
                              <td className="font-medium">{tx.vendor || tx.description}</td>
                              <td className="whitespace-nowrap"><span className="badge badge-default">{getCategoryLabel(tx.category)}</span><span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-50 text-cyan-600 border border-cyan-200">bank</span></td>
                              <td className="text-right font-medium whitespace-nowrap">{formatCurrency(tx.amount)}</td>
                              <td>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400" title="Unlink from project"
                                  onClick={async () => {
                                    try {
                                      await api.delete(`/projects/${selectedProject.project_id}/unlink-expense/${tx.expense_id}`);
                                      toast.success('Unlinked');
                                      fetchProjectExpenses(selectedProject.project_id);
                                      fetchROI(selectedProject.project_id);
                                    } catch { toast.error('Failed'); }
                                  }}>
                                  <Unlink className="w-3.5 h-3.5" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      <AddProjectDialog open={addProjectOpen} onOpenChange={(v) => { setAddProjectOpen(v); if (!v) setEditProject(null); }} ventures={ventures} locations={locations} onSuccess={fetchData} editItem={editProject} />
      {selectedProject && <AddProjectExpenseDialog open={addExpenseOpen} onOpenChange={(v) => { setAddExpenseOpen(v); if (!v) setEditExpense(null); }} project={selectedProject} editItem={editExpense} onSuccess={() => { fetchData(); fetchProjectExpenses(selectedProject.project_id); }} />}
    </div>
  );
}

function AddProjectDialog({ open, onOpenChange, ventures, locations, onSuccess, editItem }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', venture_ids: [], location_id: '', budget: '', start_date: new Date().toISOString().split('T')[0], expected_opening_date: '', loan_amount: '', loan_bank_account_id: '' });
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    api.get('/bank-accounts').then(res => setBankAccounts(res.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (editItem) {
      const vids = editItem.venture_ids || (editItem.venture_id ? [editItem.venture_id] : []);
      setForm({ name: editItem.name || '', venture_ids: vids, location_id: editItem.location_id || '', budget: String(editItem.budget || ''), start_date: editItem.start_date || '', expected_opening_date: editItem.expected_opening_date || '', loan_amount: String(editItem.loan_amount || ''), loan_bank_account_id: editItem.loan_bank_account_id || '' });
    } else setForm({ name: '', venture_ids: [], location_id: '', budget: '', start_date: new Date().toISOString().split('T')[0], expected_opening_date: '', loan_amount: '', loan_bank_account_id: '' });
  }, [editItem, open]);

  const toggleVenture = (vid) => {
    setForm(prev => ({
      ...prev,
      venture_ids: prev.venture_ids.includes(vid) ? prev.venture_ids.filter(id => id !== vid) : [...prev.venture_ids, vid],
      location_id: ''
    }));
  };

  const filteredLocations = locations.filter(l => form.venture_ids.includes(l.venture_id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || form.venture_ids.length === 0 || !form.budget) { toast.error('Fill required fields (name, ventures, budget)'); return; }
    setLoading(true);
    try {
      const payload = { ...form, budget: parseFloat(form.budget), loan_amount: form.loan_amount ? parseFloat(form.loan_amount) : null, loan_bank_account_id: form.loan_bank_account_id || null };
      if (editItem) { await api.put(`/projects/${editItem.project_id}`, payload); toast.success('Updated'); }
      else { await api.post('/projects', payload); toast.success('Created'); }
      onOpenChange(false); onSuccess();
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader><DialogTitle>{editItem ? 'Edit' : 'Create'} Project</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div><Label>Project Name</Label><Input placeholder="e.g., Chef's Table Setup" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="project-name-input" /></div>
          <div>
            <Label>Ventures (select one or more)</Label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {ventures.map((v) => (
                <button key={v.venture_id} type="button" onClick={() => toggleVenture(v.venture_id)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-all ${form.venture_ids.includes(v.venture_id) ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'}`}
                  data-testid={`venture-toggle-${v.venture_id}`}>{v.name}</button>
              ))}
            </div>
          </div>
          {filteredLocations.length > 0 && (
            <div><Label>Location (optional)</Label><Select value={form.location_id} onValueChange={(v) => setForm({ ...form, location_id: v })}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select location" /></SelectTrigger><SelectContent>{filteredLocations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}</SelectContent></Select></div>
          )}
          <div><Label>Budget</Label><div className="relative mt-1.5"><IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="pl-9" /></div></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Expected Opening</Label><Input type="date" value={form.expected_opening_date} onChange={(e) => setForm({ ...form, expected_opening_date: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div className="border-t pt-4 mt-2">
            <Label className="text-sm font-medium text-zinc-700">Loan Details (optional)</Label>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div><Label className="text-xs text-zinc-500">Loan Amount</Label><div className="relative mt-1"><IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input type="number" placeholder="e.g., 5000000" value={form.loan_amount} onChange={(e) => setForm({ ...form, loan_amount: e.target.value })} className="pl-9" data-testid="project-loan-amount" /></div></div>
              <div><Label className="text-xs text-zinc-500">Loan Bank Account</Label><Select value={form.loan_bank_account_id || 'none'} onValueChange={(v) => setForm({ ...form, loan_bank_account_id: v === 'none' ? '' : v })}><SelectTrigger className="mt-1"><SelectValue placeholder="Select account" /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{bankAccounts.map((acc) => <SelectItem key={acc.bank_account_id} value={acc.bank_account_id}>{acc.bank_name} (****{acc.account_number_masked?.slice(-4) || ''})</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Saving...' : editItem ? 'Update' : 'Create Project'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SpendByVendorCard({ projectExpenses, linkedBankExpenses }) {
  // Normalize: lowercase + collapse whitespace, so "Metalika  Metals" and "metalika metals" merge
  const normalize = (s) => (s || 'Unknown').trim().replace(/\s+/g, ' ').toLowerCase();

  const allRows = [
    ...projectExpenses.map(e => ({ vendor: (e.vendor || 'Unknown').trim(), amount: e.amount || 0, source: 'manual' })),
    ...linkedBankExpenses.map(t => ({ vendor: (t.vendor || t.description || 'Unknown').trim(), amount: t.amount || 0, source: 'bank' })),
  ];

  const byVendor = {};
  for (const r of allRows) {
    const key = normalize(r.vendor);
    if (!byVendor[key]) {
      // Pick the cleanest display name: prefer the variant with most title-case letters
      byVendor[key] = { vendor: r.vendor, _displayCandidates: [r.vendor], total: 0, count: 0, manual: 0, bank: 0 };
    } else if (!byVendor[key]._displayCandidates.includes(r.vendor)) {
      byVendor[key]._displayCandidates.push(r.vendor);
    }
    byVendor[key].total += r.amount;
    byVendor[key].count += 1;
    byVendor[key][r.source] += r.amount;
  }

  // For each vendor, pick the display name with the most capitalized words (most "proper")
  Object.values(byVendor).forEach(v => {
    v.vendor = v._displayCandidates.reduce((best, cand) => {
      const score = (s) => (s.match(/\b[A-Z]/g) || []).length;
      return score(cand) > score(best) ? cand : best;
    }, v._displayCandidates[0]);
    delete v._displayCandidates;
  });

  const summary = Object.values(byVendor).sort((a, b) => b.total - a.total);
  const grandTotal = summary.reduce((s, v) => s + v.total, 0);

  if (summary.length === 0) return null;

  return (
    <Card className="roch-card" data-testid="spend-by-vendor">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <Users className="w-4 h-4 text-zinc-500" />
          Spend by Vendor
          <span className="text-xs text-zinc-400 font-normal ml-auto">{summary.length} vendor{summary.length !== 1 ? 's' : ''} — {formatCurrency(grandTotal)}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="roch-table-wrap">
          <table className="roch-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th className="text-right">Manual</th>
                <th className="text-right">Bank-linked</th>
                <th className="text-right">Total</th>
                <th className="text-right">Txns</th>
                <th className="text-right">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((v) => (
                <tr key={v.vendor} data-testid={`vendor-row-${v.vendor}`}>
                  <td className="font-medium">{v.vendor}</td>
                  <td className="text-right text-zinc-600 whitespace-nowrap">{v.manual > 0 ? formatCurrency(v.manual) : '—'}</td>
                  <td className="text-right text-zinc-600 whitespace-nowrap">{v.bank > 0 ? formatCurrency(v.bank) : '—'}</td>
                  <td className="text-right font-semibold whitespace-nowrap">{formatCurrency(v.total)}</td>
                  <td className="text-right text-zinc-500">{v.count}</td>
                  <td className="text-right text-zinc-500">{grandTotal > 0 ? `${((v.total / grandTotal) * 100).toFixed(1)}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function AddProjectExpenseDialog({ open, onOpenChange, project, editItem, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const isEdit = !!editItem;
  const blankForm = () => ({ vendor: '', category: '', description: '', amount: '', payment_mode: 'bank_transfer', date: new Date().toISOString().split('T')[0] });
  const [form, setForm] = useState(blankForm());
  const [vendorSuggestions, setVendorSuggestions] = useState([]);

  // Populate form when editing or opening fresh
  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setForm({
        vendor: editItem.vendor || '',
        category: editItem.category || '',
        description: editItem.description || '',
        amount: String(editItem.amount ?? ''),
        payment_mode: editItem.payment_mode || 'bank_transfer',
        date: editItem.date || new Date().toISOString().split('T')[0],
      });
    } else {
      setForm(blankForm());
    }
  }, [open, editItem]);

  // Fetch existing vendors used across this project (manual + bank-linked)
  useEffect(() => {
    if (!open || !project?.project_id) return;
    (async () => {
      try {
        const [expRes, txRes] = await Promise.all([
          api.get(`/projects/${project.project_id}/expenses`),
          api.get(`/projects/${project.project_id}/all-transactions`).catch(() => ({ data: { bank_expenses: [] } })),
        ]);
        const names = new Set();
        (expRes.data || []).forEach(e => { if (e.vendor) names.add(e.vendor.trim()); });
        (txRes.data?.bank_expenses || []).forEach(t => {
          const n = (t.vendor || t.description || '').trim();
          if (n) names.add(n);
        });
        // Also pull global vendor library so the user can pick any known vendor
        try {
          const vlib = await api.get('/library/vendors');
          (vlib.data || []).forEach(v => { if (v.name) names.add(v.name); });
        } catch {}
        setVendorSuggestions(Array.from(names).sort());
      } catch {}
    })();
  }, [open, project?.project_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vendor || !form.category || !form.amount) { toast.error('Fill required fields'); return; }
    setLoading(true);
    try {
      const payload = { ...form, project_id: project.project_id, amount: parseFloat(form.amount) };
      if (isEdit) {
        await api.put(`/projects/expenses/${editItem.expense_id}`, payload);
        toast.success('Updated');
      } else {
        await api.post('/projects/expenses', payload);
        toast.success('Recorded');
      }
      setForm(blankForm());
      onOpenChange(false); onSuccess();
    } catch { toast.error(isEdit ? 'Failed to update' : 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Project Expense' : 'Add Project Expense'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label>Vendor</Label>
            <div className="mt-1.5">
              <Autocomplete
                suggestions={vendorSuggestions}
                value={form.vendor}
                onValueChange={(v) => setForm({ ...form, vendor: v })}
                placeholder="Pick an existing vendor or type a new one"
                data-testid="project-expense-vendor-input"
              />
            </div>
            <p className="text-[11px] text-zinc-400 mt-1">{vendorSuggestions.length} known vendor{vendorSuggestions.length !== 1 ? 's' : ''} — typing a new name adds it on save</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="project-expense-category-trigger"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  {Array.from(new Set(capexCategories.map(c => c.group || 'Other'))).map((groupName) => (
                    <div key={groupName}>
                      <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-zinc-400 font-semibold sticky top-0 bg-white">{groupName}</div>
                      {capexCategories.filter(c => (c.group || 'Other') === groupName).map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Payment Mode</Label><Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{paymentModes.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label>Description</Label><Input placeholder="What was this for?" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Amount</Label><div className="relative mt-1.5"><IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="pl-9" /></div></div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="save-project-expense-btn">{loading ? (isEdit ? 'Saving...' : 'Recording...') : (isEdit ? 'Save Changes' : 'Record Expense')}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
